import useScreenWidth from '@/contexts/shared/hooks/useScreenWidth'
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid'
import { Box, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CustomChip from '@/contexts/shared/components/CustomChip'
import VisibilityIcon from '@/contexts/shared/components/icons/VisibilityIcon'
import TableLanguagesCell from '@/contexts/shared/components/table/TableLanguagesCell'
import dayjs from 'dayjs'
import { showEvent, toggleFormEvent } from '@/redux/actions/events'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'

function useGetDataTable(handleOpenConfirmDeleteModal: () => void, setEventSelected: any) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)

  const columns: GridColDef[] = useMemo(() => {
    const base: GridColDef[] = [
      {
        field: 'title',
        headerName: t('title'),
        flex: 4,
        renderCell: (params: GridRenderCellParams) => (
          <Box display="flex" alignItems="center" gap={1}>
            <CustomChip size="small" color={params.row.status === 1 ? 'primary' : 'secondary'} />
            <Typography>{params.row.title}</Typography>
            <VisibilityIcon id={params.row.visibility} />
          </Box>
        )
      },
      {
        field: 'brandName',
        headerName: t('brand'),
        flex: 1,
        valueGetter: params => params.row.brand?.name || '-'
      },
      {
        field: 'language',
        headerName: t('language'),
        flex: 0.5,
        // valueGetter: params => params.row.languages?.map((l: any) => l.code.toUpperCase()).join(' - ')
        renderCell: (params: GridRenderCellParams) => <TableLanguagesCell params={params} />
      },
      {
        field: 'roleName',
        headerName: t('rol'),
        flex: 1
      },
      {
        field: 'date_ini',
        headerName: t('initial-date'),
        flex: 1,
        valueGetter: params => (params.row.date_ini ? dayjs(params.row.date_ini).format('DD MMM YYYY') : '--')
      },
      {
        field: 'date_end',
        headerName: t('final-date'),
        flex: 1,
        valueGetter: params => (params.row.date_end ? dayjs(params.row.date_end).format('DD MMM YYYY') : '--')
      },
      {
        field: 'actions',
        type: 'actions',
        sortable: false,
        filterable: false,
        width: 30,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={params.id}
            icon={<ViewIcon />}
            label={t('view')}
            onClick={() => navigate(`/admin/events/${params.id}`)}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<EditIcon />}
            label={t('edit')}
            onClick={() => {
              dispatch(showEvent(params.id!))
              dispatch(toggleFormEvent())
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<DeleteIcon />}
            label={t('delete')}
            onClick={() => {
              handleOpenConfirmDeleteModal()
              setEventSelected(params.id!)
            }}
            showInMenu
          />
        ]
      }
    ]
    return base
  }, [t, isMobile])

  return { columns }
}

export default useGetDataTable
